import { useEffect, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { useToast } from '@/hooks/use-toast'
import { Loader2, ArrowLeft, Sparkles } from 'lucide-react'

// Invites an operator who does NOT use Booked Rides: creates a miles_only
// account, maps their GHL location, and emails the standard invitation.
// The location ID is typed by the admin (trusted) — operators never enter it.

const inviteSchema = z.object({
  company_name: z.string().min(2, 'Company name is required'),
  ghl_location_id: z
    .string()
    .trim()
    .regex(/^[A-Za-z0-9]{10,}$/, 'GHL location IDs are 10+ letters/numbers'),
  email: z.string().email('Enter a valid email'),
  link_anyway: z.boolean().default(false),
})

type InviteFormData = z.infer<typeof inviteSchema>

function deriveSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
}

export function OttoOperatorInvitePage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const { user: currentUser } = useAuth()
  const [locationConflict, setLocationConflict] = useState<string | null>(null)

  const form = useForm<InviteFormData>({
    resolver: zodResolver(inviteSchema),
    defaultValues: { company_name: '', ghl_location_id: '', email: '', link_anyway: false },
  })
  const watchName = form.watch('company_name')
  const slug = deriveSlug(watchName || '')

  // Clear a stale conflict warning when the location changes.
  const watchLocation = form.watch('ghl_location_id')
  useEffect(() => setLocationConflict(null), [watchLocation])

  const mutation = useMutation({
    mutationFn: async (data: InviteFormData) => {
      const email = data.email.trim().toLowerCase()
      const location = data.ghl_location_id.trim()

      // Pre-checks (admin RLS allows all of these reads).
      const [profileRes, inviteRes, slugRes, locationRes] = await Promise.all([
        supabase.from('profiles').select('id, status').eq('email', email).maybeSingle(),
        supabase
          .from('invitations')
          .select('id')
          .eq('email', email)
          .is('accepted_at', null)
          .gt('expires_at', new Date().toISOString())
          .maybeSingle(),
        supabase.from('accounts').select('id').eq('slug', slug).maybeSingle(),
        supabase
          .from('account_settings')
          .select('account_id')
          .eq('key', 'go_high_level_location_id')
          .eq('value', location)
          .maybeSingle(),
      ])
      if (profileRes.data && profileRes.data.status === 'active') {
        throw new Error('This email already has a Booked Rides login — manage them from Users instead.')
      }
      if (inviteRes.data) {
        throw new Error('This email already has a pending invitation — revoke it on the Users page first.')
      }
      if (slugRes.data) {
        throw new Error(`An account with the slug "${slug}" already exists — tweak the company name.`)
      }
      if (locationRes.data && !data.link_anyway) {
        setLocationConflict(locationRes.data.account_id)
        throw new Error(
          'Another account is already linked to this GHL location. If that is intentional, tick "Link anyway" and submit again.'
        )
      }

      // Create the account; compensate (delete) if any later step fails.
      const { data: account, error: accountError } = await supabase
        .from('accounts')
        .insert({ name: data.company_name.trim(), slug, status: 'active', account_type: 'miles_only' })
        .select('id')
        .single()
      if (accountError) throw accountError

      try {
        const { error: settingError } = await supabase.from('account_settings').insert({
          account_id: account.id,
          key: 'go_high_level_location_id',
          value: location,
          description: 'GHL location for the Otto AI connector',
          is_secret: false,
        })
        if (settingError) throw settingError

        const { data: token, error: tokenError } = await supabase.rpc('generate_invitation_token')
        if (tokenError) throw tokenError

        const { data: invitation, error: inviteError } = await supabase
          .from('invitations')
          .insert({
            email,
            role: 'user',
            account_id: account.id,
            invited_by: currentUser?.id,
            token,
            expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
          })
          .select('id')
          .single()
        if (inviteError) throw inviteError

        const { error: emailError } = await supabase.functions.invoke('send-notification-email', {
          body: { type: 'invite', invitation_id: invitation.id },
        })
        return { token: token as string, emailSent: !emailError }
      } catch (err) {
        // Compensation: don't leave an orphan Otto account behind.
        await supabase.from('accounts').delete().eq('id', account.id)
        throw err
      }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] })
      queryClient.invalidateQueries({ queryKey: ['invitations'] })
      if (data.emailSent) {
        toast({
          title: 'Otto operator invited',
          description: 'The account was created and the invitation email is on its way.',
        })
      } else {
        toast({
          title: 'Account created — email failed',
          description: `Share this link instead: ${window.location.origin}/accept-invite/${data.token}`,
          variant: 'destructive',
        })
      }
      navigate('/admin/accounts')
    },
    onError: (error) => {
      toast({ title: 'Could not invite', description: error.message, variant: 'destructive' })
    },
  })

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to="/admin/accounts">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Invite Otto Operator</h1>
          <p className="text-muted-foreground">
            For operators who use the Otto AI connector without the full Booked Rides platform.
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5" /> New Otto-only account
          </CardTitle>
          <CardDescription>
            Creates the account, links their GoHighLevel location, and emails a sign-up invitation.
            They will see only the Otto AI portal when they log in.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit((data) => mutation.mutate(data))} className="space-y-6">
              <FormField
                control={form.control}
                name="company_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Company name</FormLabel>
                    <FormControl>
                      <Input placeholder="Acme Limo" {...field} />
                    </FormControl>
                    {slug && <FormDescription>Account slug: {slug}</FormDescription>}
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="ghl_location_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>GoHighLevel location ID</FormLabel>
                    <FormControl>
                      <Input placeholder="kH6CbjkGzYbdaYLVNJrM" {...field} />
                    </FormControl>
                    <FormDescription>
                      From the sub-account&apos;s Settings → Business Profile. Double-check it — this
                      decides whose CRM data Otto answers with.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Operator email</FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="owner@acmelimo.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {locationConflict && (
                <FormField
                  control={form.control}
                  name="link_anyway"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border border-destructive/50 p-4">
                      <FormControl>
                        <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel>Link anyway</FormLabel>
                        <FormDescription>
                          Another account already uses this location. Only continue if both accounts
                          are meant to share it.
                        </FormDescription>
                      </div>
                    </FormItem>
                  )}
                />
              )}
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Create account &amp; send invite
              </Button>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  )
}
